import { useCallback } from "react";
import { Product } from "@b2bportal/purchase-api";
import {
  cartFulfillSelectors,
  cartQuoteSelectors,
  FlightSelectors,
  getChooseSavingsProperties,
  getProductTrackingProperties,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@checkout/index";
import { trackEvent } from "@hopper-b2b/api";
import { COMPLETE_BUY_AIR } from "@hopper-b2b/types";
import {
  combineTrackingProperties,
  type ITrackingPropertiesRequest,
} from "@hopper-b2b/utilities";
import type { EventObject } from "xstate";
import {
  getBagTrackingPropertiesFromBreakdown,
  getChoosePaymentProperties,
  getConvenienceFeeLineItems,
} from "../modules/flight-checkout/selectors";
import type { FlightMachineContext } from "../modules/flight-checkout/types";

export const useTrackCompleteBuyAir = () => {
  const genericFulfillTrackingProperties = useSelector(
    cartFulfillSelectors.getCartFulfillTrackingProperties
  );
  const fulfilledProducts = useSelector(
    cartFulfillSelectors.getFulfilledProducts
  );

  return useCallback(
    (trackingProperties?: ITrackingPropertiesRequest) => {
      const fulfilledProductTrackingProperties = fulfilledProducts?.map(
        (product) => {
          return product.value.trackingProperties;
        }
      );
      const additionalProps = {
        multi_ticket_type: fulfilledProducts.find(
          (p) => p.type === Product.MultiProviderFlight
        )
          ? "hacker_fare" //TODO: add "virtual_interline" when it's ready
          : "single",
        provider: fulfilledProducts
          .filter(
            (val) =>
              val.type === Product.Flight ||
              val.type === Product.MultiProviderFlight
          )
          .reduce((acc, val) => {
            const provider =
              val.value?.travelItinerary?.locators?.agent?.provider;
            return !acc.length || acc === provider ? provider : "multiProvider";
          }, ""),
        ...(genericFulfillTrackingProperties?.properties ?? {}),
        ...(trackingProperties?.properties ?? {}),
      };

      const baseEncryptedTrackingProperties =
        genericFulfillTrackingProperties?.encryptedProperties;
      const productEncryptedTrackingProperties = fulfilledProducts
        ? fulfilledProducts
            .map(
              (product) =>
                product.value?.trackingProperties?.encryptedProperties
            )
            .filter(Boolean)
        : [];
      const customEncryptedProperties = [
        baseEncryptedTrackingProperties,
        ...(productEncryptedTrackingProperties ?? []),
        ...(trackingProperties?.encryptedProperties ?? []),
      ].filter(Boolean);

      // Combine all tracking properties
      const currTrackingProperties = combineTrackingProperties(
        fulfilledProductTrackingProperties ?? [],
        additionalProps,
        customEncryptedProperties
      );

      trackEvent({
        eventName: COMPLETE_BUY_AIR,
        properties: {
          success: true,
          ...(currTrackingProperties?.properties ?? {}),
        },
        encryptedProperties: currTrackingProperties?.encryptedProperties,
      });
    },
    [fulfilledProducts, genericFulfillTrackingProperties]
  );
};

export const useSetLocalStorageTrackingEvents = () => {
  const [state] = useCheckoutState<EventObject, FlightMachineContext>();
  const products = useSelector(cartQuoteSelectors.getCartBreakdownProductTypes);
  // NVH-371: mirror the price-change modal state into the snapshot persisted at
  // book time so it surfaces on complete_buy_air. This makes the modal-vs-charged
  // comparison self-contained (no join to price_change_shown): a decrease modal
  // (price_change_is_increase=false) whose price_change_modal_shown_total ends up
  // below the charged total_price is the NVH-371 signature.
  const priceChange = useSelector(cartQuoteSelectors.getCartQuotePriceChange);
  const convenienceFeeLineItems = useSelector(getConvenienceFeeLineItems);

  // Products for which we fetch tracking properties
  // For Disruption, we set its tracking properties even when it's not attached to the cart order. See `getDisruptionFulfillTrackingProperties`
  const trackingProducts = [
    ...new Set([
      ...(products ?? []),
      Product.AirDisruption,
      Product.AirInsurance,
    ]),
  ];
  const arrayOfTrackingProperties =
    getProductTrackingProperties<FlightMachineContext>(
      trackingProducts,
      state.context
    );

  const chooseSavingsProperties = getChooseSavingsProperties({
    context: state.context,
  });
  const choosePaymentProperties = getChoosePaymentProperties({
    context: state.context,
  });
  const priceDropViewedProperties = useSelector(
    FlightSelectors.getPriceDropProperties
  );

  const convenienceFeeTotal = convenienceFeeLineItems?.[0]?.price?.value ?? 0;
  const priceChangeProps = priceChange?.hasDifference
    ? {
        price_change_modal_shown: true,
        price_change_is_increase: priceChange.isIncrease ?? false,
        price_change_predicted_total:
          (priceChange.predictedTotal ?? 0) + convenienceFeeTotal,
        // What the modal displayed to the user ("diminuiu para {amount}").
        price_change_modal_shown_total:
          (priceChange.priceQuoteTotal ?? 0) + convenienceFeeTotal,
      }
    : { price_change_modal_shown: false };

  const additionalProps = {
    recommendation: state.context.prediction?.recommendation ?? undefined,
    ...getBagTrackingPropertiesFromBreakdown({ context: state.context }),
    ...state.context.cancelForAnyReasonV2?.aggregatedShopTrackingProperties
      ?.properties,
    ...priceChangeProps,
  };

  // Combine injected properties
  const customProperties = {
    ...(additionalProps || {}),
    ...(chooseSavingsProperties || {}),
    ...(choosePaymentProperties || {}),
    ...(priceDropViewedProperties || {}),
  };

  const cfarEncryptedTrackingProperties =
    state.context.cancelForAnyReasonV2?.aggregatedShopTrackingProperties
      ?.encryptedProperties ?? [];

  // Combine all tracking properties
  const currTrackingProperties = combineTrackingProperties(
    arrayOfTrackingProperties.flat(),
    customProperties,
    cfarEncryptedTrackingProperties
  );

  return useCallback((localStorageKey: string) => {
    // don't override what's already in storage
    if (localStorage.getItem(localStorageKey) === null) {
      localStorage.setItem(
        localStorageKey,
        JSON.stringify(currTrackingProperties)
      );
    }
  }, []);
};

export const getLocalStorageTrackingEvents = (localStorageKey: string) => {
  try {
    const prevTrackingEvents = JSON.parse(
      localStorage.getItem(localStorageKey)
    );
    if (typeof prevTrackingEvents === "object") {
      return prevTrackingEvents;
    }
  } catch {
    // the localStorageKey is empty, return empty object
    return {};
  }
};

export const removeLocalStorageTrackingEvents = (localStorageKey: string) => {
  localStorage.removeItem(localStorageKey);
};
